import { useTranslation } from '../../../i18n';
import {
  BillingKinds,
  SELECTABLE_BILLING_KINDS,
  billingKindLabelKey,
  defaultBillingRule,
  formatBillingRule,
  type BillingKind,
  type BillingRule,
} from '../../../utils/billingKind';
import { numberLocale } from '../../../utils/locale';
import { MonthSelect } from '../../Tasks/components/ScheduleRuleParts';

interface BillingRuleFieldsProps {
  kind: BillingKind | null;
  rule: BillingRule;
  onChange: (kind: BillingKind, rule: BillingRule) => void;
  error?: string;
  /** Offers "to review" as a choice — the contract import may leave the kind open. */
  allowUnknown?: boolean;
  /** The offer's object is billed quarterly: quarterly positions follow its quarters (FAFD-27). */
}

const LABEL = 'block text-[12px] font-medium text-slate-700 mb-1';
const SELECT = 'w-full px-2 py-1.5 text-[13px] border border-slate-200 rounded-md bg-white text-slate-900';
const CHIP = 'px-2.5 py-1.5 rounded-md border text-[12px] font-medium transition-colors';
const chipClass = (active: boolean) =>
  `${CHIP} ${active ? 'border-[#00A871] bg-[#00A871] text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`;

/**
 * „Abrechnung" of an offer position (FAFD-27): the kind plus, where it matters, a season
 * (monthly) or a month and in advance / in arrears (quarterly, yearly).
 */
export function BillingRuleFields({
  kind, rule, onChange, error, allowUnknown,
}: BillingRuleFieldsProps) {
  const { t } = useTranslation(['offers']);
  const set = (patch: Partial<BillingRule>) => kind && onChange(kind, { ...rule, ...patch });
  const kinds = allowUnknown ? [...SELECTABLE_BILLING_KINDS, BillingKinds.Unknown] : SELECTABLE_BILLING_KINDS;
  const hasWindow = rule.fromMonth != null && rule.toMonth != null;
  // why: computed from the rule being edited, so it follows every change before saving.
  const billedIn = formatBillingRule(kind, rule, numberLocale());

  return (
    <div className="space-y-3">
      <div>
        <div className={LABEL}>
          {t('offers:billing.label')}
          <span className="text-red-600"> *</span>
        </div>
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('offers:billing.label')}>
          {kinds.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => onChange(k, k === kind ? rule : defaultBillingRule(k))}
              className={chipClass(kind === k)}
            >
              {t(billingKindLabelKey(k))}
            </button>
          ))}
        </div>
        {kind && <p className="mt-1 text-[11px] text-slate-500">{t(`offers:billing.hints.${kind}`)}</p>}
      </div>

      {kind === BillingKinds.Monthly && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              checked={hasWindow}
              onChange={(e) => set(e.target.checked ? { fromMonth: 11, toMonth: 3 } : { fromMonth: null, toMonth: null })}
            />
            {t('offers:billing.limitSeason')}
          </label>
          {hasWindow && (
            <div className="grid grid-cols-2 gap-3">
              <MonthSelect
                label={t('offers:billing.fromMonth')}
                value={rule.fromMonth ?? 1}
                onChange={(m) => set({ fromMonth: m })}
                labelClass={LABEL}
                selectClass={SELECT}
              />
              <MonthSelect
                label={t('offers:billing.toMonth')}
                value={rule.toMonth ?? 12}
                onChange={(m) => set({ toMonth: m })}
                labelClass={LABEL}
                selectClass={SELECT}
              />
            </div>
          )}
        </div>
      )}

      {(kind === BillingKinds.Quarterly || kind === BillingKinds.Yearly) && (
        <div className="space-y-2">
          {/* why: any month, so a customer joining mid-year starts with the month the work starts. */}
          <MonthSelect
            label={t(kind === BillingKinds.Quarterly ? 'offers:billing.quarterStart' : 'offers:billing.yearMonth')}
            value={rule.month ?? 1}
            onChange={(m) => set({ month: m })}
            labelClass={LABEL}
            selectClass={SELECT}
          />
          <div>
            <div className={LABEL}>{t('offers:billing.periodLabel')}</div>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('offers:billing.periodLabel')}>
              {[false, true].map((inArrears) => (
                <button
                  key={String(inArrears)}
                  type="button"
                  role="radio"
                  aria-checked={(rule.inArrears ?? false) === inArrears}
                  onClick={() => set({ inArrears })}
                  className={chipClass((rule.inArrears ?? false) === inArrears)}
                >
                  {t(inArrears ? 'offers:billing.inArrears' : 'offers:billing.inAdvance')}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-slate-500">
              {t(rule.inArrears ? 'offers:billing.inArrearsHint' : 'offers:billing.inAdvanceHint', {
                count: kind === BillingKinds.Quarterly ? 3 : 12,
              })}
            </p>
          </div>
        </div>
      )}

      {billedIn && (
        <p className="text-[11px] text-slate-500 tabular-nums">{t('offers:billing.billedIn', { months: billedIn })}</p>
      )}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
    </div>
  );
}
